import ApiService from '@/data/services/ApiService'
import { IEvent } from '@/data/models/Event'
import { IPaginationQueryParams } from '@/data/models/Pagination'
import { AxiosResponse } from 'axios'
import { IPaginatedServiceResponse, IServiceResponse } from '@/data/models/ServiceResponse'

export interface IEventGetPaginatedQuery extends IPaginationQueryParams {
  team_id?: number;
  from?: string;
  to?: string;
  event_group_id?: number;
  player_id?: number;
  coach_id?: number;
  event_type?: string;
  exclude_event_type?: string | string[];
  compact?: boolean;
}

export class EventApiService extends ApiService {
  /**
   * Returns paginated list of Events
   *
   * @param query
   */
  static async getPaginated(query: IEventGetPaginatedQuery): Promise<IPaginatedServiceResponse<IEvent>> {
    return this.axios.get('events', { params: query })
      .then((response: AxiosResponse<IPaginatedServiceResponse<IEvent>>) => response.data)
  }

  static async getAll(query: IEventGetPaginatedQuery): Promise<IServiceResponse<IEvent>> {
    return this.axios.get('events', { params: query })
      .then((response: AxiosResponse<IServiceResponse<IEvent>>) => response.data)
  }

  /**
   * Given event's id, fetches event data from database
   *
   * @param id
   * @param params
   */
  static async getEventDetails(id: string, params: object): Promise<IServiceResponse<IEvent>> {
    return this.axios.get(`events/${id}`, {
      params
    })
      .then((response: AxiosResponse) => response.data)
  }

  /**
   * Given data, creates new entry in database
   *
   * @param eventFormData
   */
  static async create(eventFormData: IEvent): Promise<IServiceResponse<IEvent>> {
    return this.axios.post('events', eventFormData)
      .then((response: AxiosResponse<IServiceResponse<IEvent>>) => response.data)
  }

  /**
   * Given data, updates event's entry in database
   *
   * @param data
   */
  static async update(data: IEvent): Promise<IServiceResponse<IEvent>> {
    return this.axios.put(`events/${data.id}`, data)
      .then((response: AxiosResponse<IServiceResponse<IEvent>>) => response.data)
  }

  /**
   * Given event's id, deletes in database
   *
   * @param eventId
   * @param params
   */
  static async delete(eventId: string, params: object): Promise<AxiosResponse> {
    return this.axios.delete(`events/${eventId}`, {
      params
    })
  }

  static async getLatestEvent(query: object): Promise<IServiceResponse<IEvent>> {
    return this.axios.get('events/latest', { params: query })
      .then((response: AxiosResponse<IServiceResponse<IEvent>>) => response.data)
  }

  static async getEventPeriods(id: string): Promise<IServiceResponse<IEvent>> {
  return this.axios.get(`events/${id}/periods`)
    .then((response: AxiosResponse) => response.data)
}

  static async storeEventPeriods(eventId: string, data: object): Promise<IServiceResponse<object>> {
    return this.axios.post(`events/${eventId}/periods`, data)
      .then((response: AxiosResponse) => response.data)
  }

  static async storeEventPlayerDevices(eventId: string, data: object): Promise<IServiceResponse<object>> {
    return this.axios.post(`events/${eventId}/devices`, data)
      .then((response: AxiosResponse) => response.data)
  }

  static async getEventPlayerDetails(eventId: string, playerId: string): Promise<IServiceResponse<IEvent>> {
    return this.axios.get(`events/${eventId}/players/${playerId}`)
      .then((response: AxiosResponse) => response.data)
  }

  static async storeEventPlayerFeedback(eventId: string, playerId: string, data: object): Promise<IServiceResponse<object>> {
    return this.axios.post(`events/${eventId}/feedback`, {
      player_id: playerId,
      data: data
    })
      .then((response: AxiosResponse) => response.data)
  }

  static async storeEventPlayerQuestionnaireFeedback(eventId: string, playerId: string, questionnaireId: string, data: object): Promise<IServiceResponse<object>> {
    return this.axios.post(`events/${eventId}/questionnaire-feedback`, {
      player_id: playerId,
      questionnaire_id: questionnaireId,
      data: data
    })
      .then((response: AxiosResponse) => response.data)
  }

  static async storeEventPlayerNote(eventId: string, playerId: string, note: string, isNotePlayerVisible: boolean = false): Promise<IServiceResponse<object>> {
    return this.axios.post(`events/${eventId}/note`, {
      player_id: playerId,
      text: note,
      is_player_visible: isNotePlayerVisible
    })
      .then((response: AxiosResponse) => response.data)
  }

  static async storeEventPlayerTrainingExercises(eventId: string, playerId: string, exercises: object, trainingProtocolTitle: string, trainingProtocolDescription: string): Promise<IServiceResponse<object>> {
    return this.axios.post(`events/${eventId}/training-exercises`, {
      player_id: playerId,
      exercises: exercises,
      training_protocol_title: trainingProtocolTitle,
      training_protocol_description: trainingProtocolDescription
    })
      .then((response: AxiosResponse) => response.data)
  }

  static async togglePlayerFeedbackNotification(eventId: string, playerId: string, isFeedbackNotificationEnabled: boolean): Promise<IServiceResponse<object>> {
    return this.axios.post(`events/${eventId}/toggle-feedback-notification`, {
      player_id: playerId,
      is_feedback_notification_enabled: isFeedbackNotificationEnabled
    })
      .then((response: AxiosResponse) => response.data)
  }

  static async getEventMissedReasons(): Promise<IServiceResponse<object>> {
    return this.axios.get('event-missed-reasons')
      .then((response: AxiosResponse) => response.data)
  }

  static async getAdvancedPerformanceStats(params: object): Promise<IServiceResponse<object>> {
    return this.axios.get('events/advanced-performance', {
      params
    })
      .then((response: AxiosResponse) => response.data)
  }

  static async getAdvancedPerformanceTrendStats(params: object): Promise<IServiceResponse<object>> {
    return this.axios.get('events/advanced-performance-trend', {
      params
    })
      .then((response: AxiosResponse) => response.data)
  }

  static async getAdvancedPerformanceComparisonStats(params: object): Promise<IServiceResponse<object>> {
    return this.axios.get('events/advanced-performance-comparison', {
      params
    }).then((response: AxiosResponse) => response.data)
  }

  static async getDiagnosticEventVariables(eventId: string): Promise<IServiceResponse<object>> {
    return this.axios.get(`events/${eventId}/diagnostic-variables`)
      .then((response: AxiosResponse) => response.data)
  }

  static async getDiagnosticStats(eventId: string, params: object = {}): Promise<IServiceResponse<object>> {
    return this.axios.get(`events/${eventId}/diagnostic-stats`, {
      params
    })
      .then((response: AxiosResponse) => response.data)
  }

  static async getEventTeamStats(eventId: string, statsTypesAndVariables: object, extraParams: object = {}): Promise<IServiceResponse<object>> {
    return this.axios.get(`events/${eventId}/team-stats`, {
      params: {
        stats_types_variables: statsTypesAndVariables,
        ...extraParams
      }
    })
      .then((response: AxiosResponse) => response.data)
  }

  static async getPlayerPerformanceData(eventId: string, params: object): Promise<IServiceResponse<object>> {
    return this.axios.get(`events/${eventId}/player-performance-data`, {
      params
    })
      .then((response: AxiosResponse) => response.data)
  }

  static async generatePdfReport(eventId: string): Promise<IServiceResponse<object>> {
    return this.axios.get(`events/${eventId}/generate-coach-report`)
      .then((response: AxiosResponse) => response.data)
  }

  static async deletePlayerData(eventId: string, playerId: string, params: object): Promise<IServiceResponse<object>> {
    return this.axios.post(`events/${eventId}/delete-player-data`, {
      player_id: playerId,
      ...params
    })
      .then((response: AxiosResponse) => response.data)
  }

  static async deletePlayerDiagnosticsData(eventId: string, playerId: string): Promise<IServiceResponse<object>> {
    return this.axios.post(`events/${eventId}/delete-player-diagnostics-data`, {
      player_id: playerId
    })
      .then((response: AxiosResponse) => response.data)
  }

  static async deleteEventDiagnosticsData(eventId: string): Promise<IServiceResponse<object>> {
    return this.axios.post(`events/${eventId}/delete-diagnostics-data`)
      .then((response: AxiosResponse) => response.data)
  }

  static async attachDrills(eventId: string, drills: object): Promise<IServiceResponse<object>> {
    return this.axios.post(`events/${eventId}/attach-drills`, {
      drills: drills
    })
      .then((response: AxiosResponse) => response.data)
  }

  static async saveMatchPreparation(eventId: string, data: object): Promise<IServiceResponse<object>> {
    return this.axios.post(`events/${eventId}/match-preparation`, data)
      .then((response: AxiosResponse<IServiceResponse<object>>) => response.data)
  }

  static async submitNote(eventId: string, data: object): Promise<IServiceResponse<object>> {
    return this.axios.post(`events/${eventId}/note`, data)
      .then((response: AxiosResponse<IServiceResponse<object>>) => response.data)
  }

  static async getAllExercisesFromEvents(query: object): Promise<IServiceResponse<IEvent>> {
    return this.axios.get('events/training-exercises', { params: query })
      .then((response: AxiosResponse<IServiceResponse<IEvent>>) => response.data)
  }

  static async getGpsDataDownsampled(eventId: string, data: object): Promise<IServiceResponse<object>> {
    return this.axios.get(`events/${eventId}/gps-downsampled`, {
      params: data
    }).then((response: AxiosResponse<IServiceResponse<object>>) => response.data)
  }

  static async getMatchReferenceData(eventId: string, data: object): Promise<IServiceResponse<object>> {
    return this.axios.get(`events/${eventId}/match-reference`, {
      params: data
    }).then((response: AxiosResponse<IServiceResponse<object>>) => response.data)
  }

  static async getEventPlanPdf(eventId: string): Promise<IServiceResponse<object>> {
    return this.axios.get(`events/${eventId}/generate-event-plan`)
      .then((response: AxiosResponse<IServiceResponse<object>>) => response.data)
  }

  static async getMatchPreparationPdf(eventId: string): Promise<IServiceResponse<object>> {
    return this.axios.get(`events/${eventId}/generate-match-preparation`)
      .then((response: AxiosResponse<IServiceResponse<object>>) => response.data)
  }

  static async getEventPlanFootballPdf(eventId: string): Promise<IServiceResponse<object>> {
    return this.axios.get(`events/${eventId}/generate-event-plan-football`)
      .then((response: AxiosResponse<IServiceResponse<object>>) => response.data)
  }

  static async getMassageTreatmentTypes(): Promise<IServiceResponse<object>> {
    return this.axios.get('events/massage-treatment-types')
      .then((response: AxiosResponse<IServiceResponse<object>>) => response.data)
  }

  static async getMassageStatsByPlayer(params: object): Promise<IServiceResponse<object>> {
    return this.axios.get('events/player-massage-stats', {
      params
    }).then((response: AxiosResponse<IServiceResponse<object>>) => response.data)
  }

  static async getPlayerStats(eventId: number, params: {
    player_ids?: number[],
    stats_types_variables: object,
    player_status_id?: number,
    player_position_id?: number
  }): Promise<object> {
    return this.axios.get(`events/${eventId}/player-stats`, { params }).then(response => response.data)
  }
}
